import React, { useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  Filler,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";

import { cn } from "../lib/utils";
import { formatFiatWithTwoDecimals } from "../utils/numberFormatter";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler
);

const DEFAULT_TICK_COUNT = 8;
const LINE_GRADIENT_STOPS = [
  { offset: 0, color: "#57249C" },
  { offset: 1, color: "#EB3366" },
];

const getDefaultActivePointIndex = (length) =>
  length > 4 ? Math.floor(length / 2) : Math.max(length - 1, 0);

const getAxisBounds = (values) => {
  if (values.length === 0) {
    return {
      min: 0,
      max: 1,
    };
  }

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  if (minValue === maxValue) {
    const offset = Math.max(Math.abs(minValue) * 0.08, 1);

    return {
      min: minValue - offset,
      max: maxValue + offset,
    };
  }

  const padding = Math.max((maxValue - minValue) * 0.14, 1);

  return {
    min: minValue - padding,
    max: maxValue + padding,
  };
};

const formatAxisLabel = (value) => {
  if (Math.abs(value) >= 1000000) {
    return `${(value / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  }

  if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  }

  return `${Math.round(value)}`;
};

function ChartState({ title, body, actionLabel, onAction }) {
  return (
    <div className="flex h-[438px] items-center justify-center rounded-[24px] border border-dashed border-[#EBE9EF] bg-[#FAF9FC] px-6 text-center">
      <div>
        <p className="text-base font-semibold leading-6 text-[#423E51]">
          {title}
        </p>
        <p className="mt-2 text-sm leading-5 text-[#6D6584]">{body}</p>
        {actionLabel ? (
          <button
            type="button"
            onClick={onAction}
            className="mt-5 inline-flex h-10 items-center justify-center rounded-full border border-[#EBE9EF] px-5 text-sm font-semibold leading-5 text-[#6D6584] transition-colors hover:bg-[#FFFFFF]"
          >
            {actionLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function Zar8AssetPerformanceChart({
  series = [],
  isLoading = false,
  hasError = false,
  onRetry,
}) {
  const chartAreaRef = useRef(null);
  const [activePointIndex, setActivePointIndex] = useState(() =>
    getDefaultActivePointIndex(series.length)
  );
  const labels = useMemo(
    () => series.map((point) => point.shortLabel),
    [series]
  );
  const values = useMemo(() => series.map((point) => point.value), [series]);
  const activePoint = series.at(activePointIndex) || null;
  const axisBounds = useMemo(() => getAxisBounds(values), [values]);
  const axisTickLabels = useMemo(() => {
    const tickStep =
      (axisBounds.max - axisBounds.min) / Math.max(DEFAULT_TICK_COUNT - 1, 1);

    return Array.from({ length: DEFAULT_TICK_COUNT }, (_, index) =>
      formatAxisLabel(axisBounds.max - tickStep * index)
    );
  }, [axisBounds.max, axisBounds.min]);

  useEffect(() => {
    setActivePointIndex(getDefaultActivePointIndex(series.length));
  }, [series.length]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          data: values,
          fill: true,
          borderColor: (context) => {
            const chart = context.chart;
            const { ctx, chartArea } = chart;

            if (!chartArea) {
              return LINE_GRADIENT_STOPS[0].color;
            }

            const gradient = ctx.createLinearGradient(
              chartArea.left,
              chartArea.top,
              chartArea.right,
              chartArea.top
            );

            LINE_GRADIENT_STOPS.forEach(({ offset, color }) => {
              gradient.addColorStop(offset, color);
            });

            return gradient;
          },
          backgroundColor: (context) => {
            const chart = context.chart;
            const { ctx, chartArea } = chart;

            if (!chartArea) {
              return "rgba(235, 51, 102, 0.08)";
            }

            const gradient = ctx.createLinearGradient(
              chartArea.left,
              chartArea.top,
              chartArea.right,
              chartArea.bottom
            );

            gradient.addColorStop(0, "rgba(87, 36, 156, 0.03)");
            gradient.addColorStop(1, "rgba(235, 51, 102, 0.12)");

            return gradient;
          },
          tension: 0.38,
          borderWidth: 1.6,
          pointRadius: (context) =>
            context.dataIndex === activePointIndex ? 7 : 0,
          pointHoverRadius: (context) =>
            context.dataIndex === activePointIndex ? 7 : 0,
          pointBackgroundColor: "#EB3366",
          pointBorderColor: "#FFFFFF",
          pointBorderWidth: 3,
          pointHitRadius: 18,
        },
      ],
    }),
    [activePointIndex, labels, values]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      layout: {
        padding: 0,
      },
      scales: {
        x: {
          display: false,
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
        },
        y: {
          min: axisBounds.min,
          max: axisBounds.max,
          display: false,
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
        },
      },
      interaction: {
        intersect: false,
        mode: "index",
      },
    }),
    [axisBounds.max, axisBounds.min]
  );

  const handleChartMouseMove = (event) => {
    if (!chartAreaRef.current || series.length === 0) {
      return;
    }

    const bounds = chartAreaRef.current.getBoundingClientRect();
    const relativeX = Math.min(
      Math.max(event.clientX - bounds.left, 0),
      bounds.width
    );
    const nextIndex = Math.round(
      (relativeX / Math.max(bounds.width, 1)) * Math.max(series.length - 1, 0)
    );

    setActivePointIndex(nextIndex);
  };

  if (isLoading) {
    return (
      <div className="h-[438px] animate-pulse rounded-[24px] bg-[#F8F6FB]" />
    );
  }

  if (hasError) {
    return (
      <ChartState
        title="Performance unavailable"
        body="We couldn’t load the ZAR8 performance chart right now."
        actionLabel="Retry"
        onAction={onRetry}
      />
    );
  }

  if (series.length === 0) {
    return (
      <ChartState
        title="No performance history yet"
        body="Performance data will appear here once market history becomes available."
      />
    );
  }

  return (
    <div className="relative h-[438px] w-full">
      <div className="absolute left-0 top-[8px] flex h-[356px] w-[26px] flex-col justify-between text-right text-[12px] leading-[14px] text-[#9D96AF]">
        {axisTickLabels.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>

      <div
        ref={chartAreaRef}
        className="absolute bottom-[34px] left-[46px] right-0 top-[8px]"
        onMouseMove={handleChartMouseMove}
        onMouseLeave={() =>
          setActivePointIndex(getDefaultActivePointIndex(series.length))
        }
      >
        <div className="absolute inset-0 grid grid-rows-8">
          {Array.from({ length: 8 }).map((_, index) => (
            <div
              key={index}
              className="border-t border-[#F1EDF7] opacity-80 first:border-t-0"
            />
          ))}
        </div>

        <Line data={data} options={options} />

        {activePoint ? (
          <>
            <div
              className="absolute bottom-[12px] top-0 border-l border-dashed border-[#57249C]"
              style={{
                left: `${(activePointIndex / Math.max(series.length - 1, 1)) * 100}%`,
                transform: "translateX(-0.5px)",
              }}
            />
            <div
              className="absolute rounded-[8px] bg-[#EB3366] px-2 py-2 text-[12px] font-semibold leading-[14px] text-white shadow-[0_12px_30px_rgba(235,51,102,0.18)]"
              style={{
                left: `${(activePointIndex / Math.max(series.length - 1, 1)) * 100}%`,
                top: "8px",
                transform: "translate(-50%, -100%)",
              }}
            >
              <span>{activePoint.tooltipLabel || activePoint.fullLabel}</span>
              <span className="ml-[5px] font-bold">
                {formatFiatWithTwoDecimals(activePoint.value)}
              </span>
            </div>
          </>
        ) : null}
      </div>

      <div className="absolute bottom-0 left-[46px] right-0 flex items-start justify-between text-center text-[12px] leading-[14px] text-[#9D96AF]">
        {labels.map((label, index) => (
          <span
            key={`${label}-${index}`}
            className={cn(
              "min-w-[28px]",
              index === activePointIndex && "font-bold text-[#6D6584]"
            )}
          >
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}
